import { queriesAccounts, queriesPayStatements, queriesWorkLogs } from "@query";
import {
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";

import { WSQueries } from "../../../../query/WSQuery";
import { PaystubDeductionsCard } from "../../../PayrollRuns/components/PaystubDeductionsCard";
import { PaystubIncomeCard } from "../../../PayrollRuns/components/PaystubIncomeCard";
import { useMutationDownloadPaystubPdf } from "../../../PayrollRuns/hooks/useMutationDownloadPaystubPdf";
import { PaystubDetailsCard } from "../../components/PaystubDetailsCard";
import { PaystubWorkLogsCard } from "../../components/PaystubWorkLogsCard";
import {
  routeHelperPaystubsDashboard,
  routeHelperPaystubsDetails
} from "../../paths";
import { selectorPaystubDetails } from "../../selectors/selectorPaystubDetails";
import { PaystubsYear } from "../../types";

export const RoutePaystubsDetails: React.FC = () => {
  const goToDashboard = routeHelperPaystubsDashboard.useGoTo();
  const [downloadPaystubPdf, downloadPaystubPdfMeta] =
    useMutationDownloadPaystubPdf();
  const {
    params: { year, paystubId }
  } = routeHelperPaystubsDetails.useData();

  const queryPayStatement = queriesPayStatements.getPayStatement.useQuery({
    payStatementId: paystubId
  });
  const payPeriodId = queryPayStatement.data?.payPeriodId ?? undefined;

  // Secondary queries stay OUTSIDE `WSQueries` (as unconditional, `enabled`-gated
  // hooks) — none becomes `isSuccess` on its own, so they'd otherwise keep
  // `WSQueries` stuck on the loader. Their data is consumed with fallbacks below.
  //
  // The work-logs API has no server-side pay-period filter, so we fetch the list
  // and filter client-side by payPeriodId to resolve this statement's work logs.
  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQueryAll(
    {},
    { enabled: !!payPeriodId }
  );

  // The paying company ("Created by {company}" / employer name) is a separate
  // read — the pay statement carries only the payer's account id.
  const payerAccountId = queryPayStatement.data?.payerAccountId ?? undefined;
  const queryPayerAccount = queriesAccounts.getAccount.useQuery(
    { accountId: payerAccountId ?? "" },
    { enabled: !!payerAccountId }
  );

  return (
    <WSQueries queries={{ queryPayStatement }}>
      {({ queryPayStatementData }) => {
        const payPeriodWorkLogs = (queryWorkLogs.flatData ?? []).filter(
          workLog => workLog.payPeriodId === queryPayStatementData.payPeriodId
        );

        const paystub = selectorPaystubDetails({
          payStatement: queryPayStatementData,
          workLogs: payPeriodWorkLogs,
          payerAccount: queryPayerAccount.data
        });

        const fileId = queryPayStatementData.fileId ?? undefined;
        const handleDownloadPdf = () =>
          fileId &&
          downloadPaystubPdf({
            fileId,
            filename: `paystub-${paystub.paystubNumber}`
          });

        return (
          <WSPage>
            <WSPageToolbar
              actions={[
                {
                  label: "Download PDF",
                  buttonKind: "Secondary",
                  disabled: !fileId,
                  loading: downloadPaystubPdfMeta.isLoading,
                  onClick: handleDownloadPdf
                },
                { separator: true },
                { label: "Download CSV", icon: "download" }
              ]}
              breadcrumbs={[
                {
                  label: "Paystubs",
                  onClick: () => goToDashboard({ year: year as PaystubsYear })
                },
                { label: `Paystub #${paystub.paystubNumber}` }
              ]}
              title={`Paystub #${paystub.paystubNumber}`}
              titleAfter={
                <WSText.ParagraphXs color="gray500">
                  Created by {paystub.createdBy}
                </WSText.ParagraphXs>
              }
            />

            <WSGrid gutter="L" mt="XL">
              <WSGrid.Item span={{ s: "12", l: "8" }}>
                <WSList gap="XL">
                  <PaystubIncomeCard
                    earnings={paystub.earnings}
                    grossPayThisPeriod={paystub.grossPayThisPeriod}
                    grossPayYearToDate={paystub.grossPayYearToDate}
                  />
                  <PaystubDeductionsCard
                    deductionGroups={paystub.deductionGroups}
                    deductionTotalThisPeriod={paystub.deductionTotalThisPeriod}
                    deductionTotalYearToDate={paystub.deductionTotalYearToDate}
                    netPayThisPeriod={paystub.netPayThisPeriod}
                    netPayYearToDate={paystub.netPayYearToDate}
                    payoutMethodLabel={paystub.payoutMethodLabel}
                  />
                </WSList>
              </WSGrid.Item>

              <WSGrid.Item span={{ s: "12", l: "4" }}>
                <WSList gap="L">
                  <PaystubDetailsCard
                    employerName={paystub.employerName}
                    paidDate={paystub.paidDate}
                    paymentMethodLabel={paystub.paymentMethodLabel}
                    paymentSplit={paystub.paymentSplit}
                    payPeriodRange={paystub.payPeriodRange}
                  />
                  <PaystubWorkLogsCard
                    workLogs={paystub.workLogs}
                    workLogsTotal={paystub.workLogsTotal}
                  />
                </WSList>
              </WSGrid.Item>
            </WSGrid>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
